import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Box, List, ListItem, ListItemText, IconButton,
    CircularProgress, Divider, Paper
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CloudDownload as DownloadIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    InsertDriveFile as FileIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../api/api'; 
import { saveAs } from 'file-saver';
import VisorFicherosModal from './VisorFicherosModal';

export default function FicherosPacienteModal({ open, onClose, pacienteId, nombrePaciente }) {
    const [ficheros, setFicheros] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);

    const [visorOpen, setVisorOpen] = useState(false);
    const [visorUrl, setVisorUrl] = useState('');
    const [visorNombre, setVisorNombre] = useState('');

    const cargarFicheros = useCallback(async () => {
        if (!pacienteId) return;
        setCargando(true);
        try {
            const response = await api.get(`/ficheros/paciente/${pacienteId}`);
            setFicheros(response.data);
        } catch (error) {
            console.error("Error al cargar ficheros:", error);
        } finally {
            setCargando(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        if (open) {
            cargarFicheros();
        }
    }, [open, cargarFicheros]);

    const onDrop = useCallback(async (acceptedFiles, fileRejections) => {
        // Manejar si react-dropzone bloqueó el archivo por exceder el tamaño
        if (fileRejections.length > 0) {
            alert("El archivo fue rechazado. El tamaño máximo permitido es de 3 MB o no tiene el formato permitido");
            return;
        }

        if (acceptedFiles.length === 0) return;
        
        setSubiendo(true);
        const file = acceptedFiles[0];
        
        const formData = new FormData();
        formData.append('id_paciente', pacienteId);
        formData.append('file', file);
        formData.append('desc_fichero', `Adjunto de ${nombrePaciente}`);

        try {
            await api.post('/ficheros/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            cargarFicheros();
        } catch (error) {
            console.error("Error detallado en la subida:", error.response?.data || error.message);
            alert(`No se pudo subir el archivo: ${error.response?.data?.detail || error.message}`);
        } finally {
            setSubiendo(false);
        }
    }, [pacienteId, nombrePaciente, cargarFicheros]);

    // Configuramos react-dropzone con maxSizeBytes (3 MB)
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        maxSize: 3 * 1024 * 1024, // 3 Megabytes estrictos en el cliente
        accept: {
        'application/pdf': ['.pdf'],
        'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
        }
    });

    const handleDownload = async (idFichero, nombreOriginal, extension) => {
        try {
            const response = await api.get(`/ficheros/download/${idFichero}`, {
                responseType: 'blob'
            });
            saveAs(response.data, `${nombreOriginal}.${extension}`);
        } catch (error) {
            console.error("Error al descargar archivo:", error);
            alert("Error al intentar descargar el archivo.");
        }
    };

    // Función para ejecutar el borrado lógico en el Backend
    const handleEliminar = async (idFichero) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este archivo del expediente?")) return;

        try {
            await api.delete(`/ficheros/delete/${idFichero}`);
            cargarFicheros(); // Refrescar lista de archivos activos
        } catch (error) {
            console.error("Error al eliminar archivo:", error);
            alert("No se pudo eliminar el archivo.");
        }
    };

    const formatearTamanio = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const handleVisualizar = async (fichero) => {
    try {
        const response = await api.get(`/ficheros/download/${fichero.id_fichero}`, {
            responseType: 'blob'
        });
        
        const blobUrl = URL.createObjectURL(response.data);
        const nombreCompleto = `${fichero.nombre_fichero}.${fichero.extension_fichero}`;

        // Ahora ambos formatos abren directamente tu visor integrado
        setVisorUrl(blobUrl);
        setVisorNombre(nombreCompleto);
        setVisorOpen(true);
    } catch (error) {
        console.error("Error al visualizar archivo:", error);
        alert("No se pudo cargar la vista previa.");
    }
};

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div">
                    Expediente Digital: {nombrePaciente}
                </Typography>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Paper
                    {...getRootProps()}
                    variant="outlined"
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                        borderStyle: 'dashed',
                        borderColor: isDragActive ? 'primary.main' : 'divider',
                        transition: 'all 0.2s ease',
                        mb: 3
                    }}
                >
                    <input { ...getInputProps() } />
                    {subiendo ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Subiendo y guardando en volumen...</Typography>
                        </Box>
                    ) : (
                        <>
                            <UploadIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="body1">
                                {isDragActive ? "Suelte el archivo aquí..." : "Arrastre un archivo o haga clic aquí "}                               
                            </Typography>
                            <Typography variant="body1">
                                {isDragActive ? "Solo": ".pdf, .png, .jpg, .jpeg, .gif, .webp "}                               
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Máximo 3 MB por archivo
                            </Typography>
                        </>
                    )}
                </Paper>

                <Typography variant="subtitle2" gutterBottom>
                    Archivos guardados ({ficheros.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />

                {cargando ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : ficheros.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No hay archivos cargados para esta mascota.
                    </Typography>
                ) : (
                    <List dense>
                        {ficheros.map((fichero) => (
                            <ListItem
                                key={fichero.id_fichero}
                                secondaryAction={
                                    <Box>
                                        <IconButton 
                                            edge="end" 
                                            aria-label="download"
                                            onClick={() => handleDownload(fichero.id_fichero, fichero.nombre_fichero, fichero.extension_fichero)}
                                            color="primary"
                                            sx={{ mr: 1 }}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                        <IconButton 
                                            edge="end" 
                                            aria-label="view"
                                            onClick={() => handleVisualizar(fichero)}
                                            color="info"
                                            sx={{ mr: 1 }}
                                        >
                                            <ViewIcon />
                                        </IconButton>
                                        <IconButton 
                                            edge="end" 
                                            aria-label="delete"
                                            onClick={() => handleEliminar(fichero.id_fichero)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <FileIcon sx={{ mr: 2, color: 'action.active' }} />
                                <ListItemText
                                    primary={`${fichero.nombre_fichero}.${fichero.extension_fichero}`}
                                    secondary={`${fichero.desc_fichero || ''} • ${formatearTamanio(fichero.tamanio_fichero)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cerrar</Button>
            </DialogActions>

            {visorOpen && (
                <VisorFicherosModal
                    open={visorOpen}
                    onClose={() => {
                        setVisorOpen(false);
                        URL.revokeObjectURL(visorUrl); // Liberar memoria del navegador
                        setVisorUrl('');
                    }}
                    archivoUrl={visorUrl}
                    nombreArchivo={visorNombre}
                />
            )}
        </Dialog>
    );
}